import React, { useMemo, useState } from 'react';
import {
    Alert, Box, Chip, FormControlLabel, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { aiAdminTokens as t } from './tokens';

const CHIP_SX = { height: 22, fontSize: 11.5, fontFamily: t.font.sans };

// El servicio tiene cientos de specs (las versiones viejas del prompt conviven
// con las nuevas), así que la pestaña es una grilla con buscador y no una lista
// de tarjetas: sin filtrar, encontrar el que se busca es imposible.
//
// Es de consulta: con cuál se clasifica se decide por corrida en la pantalla
// Label, no acá.
export default function SpecsTab({ tr, specs }) {
    const [search, setSearch] = useState('');
    // Los specs no utilizables son sobre todo entradas legacy previas al
    // refactor de prompts: no se pueden correr, así que vienen ocultos.
    const [onlyUsable, setOnlyUsable] = useState(true);

    const rows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return (specs.data?.specs ?? [])
            .filter((s) => (!onlyUsable || s.usable))
            .filter((s) => !needle || [s.name, ...(s.llm_members ?? []), ...(s.providers ?? [])]
                .join(' ').toLowerCase().includes(needle))
            .map((s) => ({ ...s, id: s.name }));
    }, [specs.data, search, onlyUsable]);

    const columns = useMemo(() => [
        { field: 'name', headerName: tr('name'), flex: 1.6, minWidth: 260 },
        {
            field: 'llm_members',
            headerName: tr('ai_admin_members'),
            flex: 1.2,
            minWidth: 180,
            sortable: false,
            valueGetter: (value) => (value?.length ? value.join(', ') : '—'),
        },
        {
            field: 'synthesis_llm',
            headerName: tr('ai_admin_synthesis'),
            width: 150,
            sortable: false,
            valueGetter: (value, row) => (row.enable_synthesis && value ? value : '—'),
        },
        {
            field: 'providers',
            headerName: tr('ai_admin_providers'),
            width: 150,
            sortable: false,
            valueGetter: (value) => (value?.length ? value.join(', ') : '—'),
        },
        {
            field: 'status',
            headerName: '',
            width: 150,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', height: '100%' }}>
                    {params.row.is_ensemble && (
                        <Chip label={tr('ai_admin_ensemble')} size="small"
                              sx={{ ...CHIP_SX, bgcolor: t.color.chipBg }}/>
                    )}
                    {!params.row.usable && (
                        <Tooltip title={params.row.unusable_reason || ''}>
                            <Chip label={tr('ai_admin_unusable')} size="small"
                                  sx={{ ...CHIP_SX, bgcolor: t.color.chipBg, color: t.color.danger }}/>
                        </Tooltip>
                    )}
                    {params.row.usable && !params.row.credentials_available && (
                        <Tooltip title={tr('ai_admin_no_credentials_help')}>
                            <Chip label={tr('ai_admin_no_credentials')} size="small"
                                  sx={{ ...CHIP_SX, bgcolor: t.color.warnSoft, color: t.color.warn }}/>
                        </Tooltip>
                    )}
                </Box>
            ),
        },
    ], [tr]);

    if (specs.error) {
        return (
            <Alert severity="error">
                {typeof specs.error === 'string' ? specs.error : tr('ai_admin_service_down')}
            </Alert>
        );
    }

    return (
        <Box>
            <Typography sx={{ fontFamily: t.font.sans, fontSize: 12.5, mb: 1.5 }}>
                {tr('ai_admin_specs_note')}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={tr('ai_admin_search_specs')}
                    sx={{ minWidth: 260, bgcolor: t.color.surface }}
                />
                <FormControlLabel
                    control={<Switch size="small" checked={onlyUsable}
                                     onChange={(e) => setOnlyUsable(e.target.checked)}/>}
                    label={
                        <Typography sx={{ fontFamily: t.font.sans, fontSize: 13 }}>
                            {tr('ai_admin_only_usable')}
                        </Typography>
                    }
                />
                <Typography sx={{ fontFamily: t.font.sans, fontSize: 12.5, ml: 'auto' }}>
                    {`${rows.length} ${tr('ai_admin_specs').toLowerCase()}`}
                </Typography>
            </Box>

            <DataGrid
                rows={rows}
                columns={columns}
                loading={specs.loading}
                autoHeight
                disableRowSelectionOnClick
                hideFooterSelectedRowCount
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                pageSizeOptions={[25, 50, 100]}
                localeText={{
                    MuiTablePagination: { labelRowsPerPage: tr('rows_per_page') },
                    noRowsLabel: tr('ai_admin_no_specs'),
                }}
                sx={{
                    bgcolor: t.color.surface,
                    border: `1px solid ${t.color.border}`,
                    borderRadius: `${t.radius.lg}px`,
                    fontFamily: t.font.sans,
                    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, fontSize: 12.5 },
                    '& .MuiDataGrid-cell': { fontSize: 13 },
                }}
            />
        </Box>
    );
}
